import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { SelectionsModel, ControlTypeEnum } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { VennHealthDialogService } from '@app/services/vennhealth-dialog.service';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { first, skip, asapScheduler } from 'rxjs';

@Component({
  selector: 'app-diabetes',
  templateUrl: './diabetes.component.html',
  styleUrls: ['./diabetes.component.css']
})
export class DiabetesComponent implements OnInit {

  @ViewChild('childDiagnosisComponent') childDiagnosisComponent: RadioInputGroupComponent;
  @ViewChild('childPopulationsComponent') childPopulationsComponent: RadioInputGroupComponent;

  @Input() indication: IndicationDefinition;

  // copy of indication where we add the diagnosis inputOptions to pass to the 1st radio button child component
  diagnosisIndication: IndicationDefinition;

  // copy of indication where we add the populations inputOptions to pass to the 1st radio button child component
  populationsIndication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];

  childDiagnosisSelectionModel: SelectionsModel;
  childPopulationsSelectionModel: SelectionsModel;

  fastingPlasmaGlucoseLevelLabel: string = 'Fasting Plasma Glucose Level Upper Limit';
  glycohemoglobinLabel: string = 'Glycohemoglobin Lower Limit';
  diagnosedDiabeticRetinopathyLabel: string = 'Diagnosed Diabetic Retinopathy';

  diagnosedDiabeticRetinopathyVariableName: string = 'diagnosedDiabeticRetinopathy';

  diagnosisInputOptions: InputOption[] = [
    {name: 'D', displayName: 'Diabetes', order: 1, defaultValue: 'D'},
    {name: 'P', displayName: 'Prediabetes', order: 2, defaultValue: 'D'}
  ];

  populationsInputOptions: any[] = [
    {name: 'DO', displayName: 'Diagnosed Only', order: 1, defaultValue: 'DU'},
    {name: 'MC', displayName: 'Meets Criteria (All Patients)', order: 2, defaultValue: 'DU', checkbox: [
        { name: 'MC', displayName: 'Meets Criteria Only', order: 1},
        { name: 'MC_AM', displayName: 'Add patients taking Insulin/Pills', order: 2 }, 
        { name: 'MC_LM', displayName: 'Limit to patients taking Insulin/Pills', order: 3}
      ]
    },
    {name: 'UO', displayName: 'Undiagnosed Only (Meets Criteria)', order: 3, defaultValue: 'DU'},
    {name: 'DU', displayName: 'Diagnosed + Undiagnosed (Meets Criteria)', order: 4, defaultValue: 'DU'},
    {name: 'TO', displayName: 'Treated Patients Only', order: 5, defaultValue: 'DU'}
  ];

  diabetesInputOptions: InputOption[][] = [
    [
      {name: 'AND', displayName: 'AND', order: 1},
      {name: 'OR', displayName: 'OR', order: 2}
    ],
    [
      {name: 'fastingPlasmaGlucoseLevel', displayName: this.fastingPlasmaGlucoseLevelLabel, order: 1, defaultValue: 126, maxlength: 5, measurement: '(mg/dL)', details: '(<)'},
      {name: 'glycohemoglobin', displayName: this.glycohemoglobinLabel, order: 2, defaultValue: 6.5, maxlength: 4, measurement: '(% HbA1c)', details: '(>=)'}
    ]
  ];

  diagnosedDiabeticRetinopathyRadioInputOptions: InputOption[] = [
    {name: 'no', displayName: 'No', order: 1, defaultValue: 'yes'},
    {name: 'yes', displayName: 'Yes', order: 2, defaultValue: 'yes'}
  ];

  public formGroup: FormGroup = this.getDefaultFormGroup();

  constructor(
    private sharedService: SharedService,
    private readonly vennHealthDialogService: VennHealthDialogService
  ) { }

  ngOnInit() {
    const updatedFPGLabel = 'Fasting Plasma Glucose Level (mg/dL) Upper Limit (<)';
    const updatedGlycohemoglobinLabel = 'Glycohemoglobin (% HbA1c) Lower Limit (>=)';

    this.diabetesInputOptions[1][0].displayName = updatedFPGLabel;
    this.diabetesInputOptions[1][1].displayName = updatedGlycohemoglobinLabel;
    this.assignChildIndicationDetails();

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });

    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.assignChildIndicationDetails();
        this.setIndicationSelections();
      }
    });
  }

  assignChildIndicationDetails() {
    this.populationsIndication = Object.assign({}, this.indication);
    this.populationsIndication.selections = this.populationsIndication.selections.filter(i => i.controlName === 'populations');
    this.populationsIndication.inputOptions = this.populationsInputOptions;
    if (this.populationsIndication.selections && this.populationsIndication.selections.length > 0) {
      this.childPopulationsSelectionModel = this.populationsIndication.selections[0];
    }

    this.diagnosisIndication = Object.assign({}, this.indication);
    this.diagnosisIndication.selections = this.diagnosisIndication.selections.filter(i => i.controlName === 'diagnosis');
    this.diagnosisIndication.inputOptions = this.diagnosisInputOptions;
    if (this.diagnosisIndication.selections && this.diagnosisIndication.selections.length > 0) {
      this.childDiagnosisSelectionModel = this.diagnosisIndication.selections[0];
    }
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {

      // child populations radio button group
      if (this.indication.selections.some(i => i.controlName === 'populations')) {
        if (this.childPopulationsComponent) {
          this.childPopulationsComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'populations').selectionValue);
        }
      }

      // child diagnosis radio button group
      if (this.indication.selections.some(i => i.controlName === 'diagnosis')) {
        if (this.childDiagnosisComponent) {
          this.childDiagnosisComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'diagnosis').selectionValue);
        }
      }

      if (this.formGroup) {
        // diagnosed diabetic retinopathy radio buttons
        if (this.indication.selections.some(i => i.controlName === this.diagnosedDiabeticRetinopathyVariableName)){
          this.formGroup.controls.diagnosedDiabeticRetinopathy.setValue(this.indication.selections.find(i => i.controlName === this.diagnosedDiabeticRetinopathyVariableName).selectionValue);
        }

        if (this.indication.selections.some(i => i.controlName === 'fastingPlasmaGlucoseLevelCheckbox')){
          this.formGroup.controls.fastingPlasmaGlucoseLevelCheckbox.setValue(this.indication.selections.find(i => i.controlName === 'fastingPlasmaGlucoseLevelCheckbox').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'glycohemoglobinCheckbox')){
          this.formGroup.controls.glycohemoglobinCheckbox.setValue(this.indication.selections.find(i => i.controlName === 'glycohemoglobinCheckbox').selectionValue);
        }

        // inputs
        if (this.indication.selections.some(i => i.controlName === 'fastingPlasmaGlucoseLevel')){
          this.formGroup.controls.fastingPlasmaGlucoseLevel.setValue(this.indication.selections.find(i => i.controlName === 'fastingPlasmaGlucoseLevel').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'condition')){
          this.formGroup.controls.condition.setValue(this.indication.selections.find(i => i.controlName === 'condition').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'glycohemoglobin')){
          this.formGroup.controls.glycohemoglobin.setValue(this.indication.selections.find(i => i.controlName === 'glycohemoglobin').selectionValue);
        }
      }
      this.updateSelections();
    } else {
      this.setDefaults();
    }
  }

  childSharedDiagnosisControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childDiagnosisSelectionModel = selectionModel;
    this.updateSelections();
  }

  childSharedPopulationsControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childPopulationsSelectionModel = selectionModel;
    this.updateSelections();
  }

  showDiagnosisSelections(): boolean {
    const diagnosisSelectionValues = ['DO', 'UO', 'DU'];
    if (this.childPopulationsSelectionModel && diagnosisSelectionValues.includes(this.childPopulationsSelectionModel.selectionValue)) {
      return true;
    }
    return false;
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      if (this.childPopulationsComponent) {
        this.childPopulationsComponent.overrideInputValue('DU');
      }
      if (this.showDiagnosisSelections()) {
        this.childDiagnosisComponent.overrideInputValue('D');
      }
      this.formGroup = this.getDefaultFormGroup();
      this.updateSelections();
    });
  }

  getDefaultFormGroup(): FormGroup {
    const fg = new FormGroup({
      diagnosedDiabeticRetinopathy: new FormControl(this.diagnosedDiabeticRetinopathyRadioInputOptions[0].defaultValue, [Validators.required]),
      fastingPlasmaGlucoseLevelCheckbox: new FormControl(true, []),
      fastingPlasmaGlucoseLevel: new FormControl(126, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]),
      condition: new FormControl('OR', [
        Validators.required
      ]),
      glycohemoglobinCheckbox: new FormControl(true, []),
      glycohemoglobin: new FormControl(7, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ])
    });
    return fg;
  }

  fastingPlasmaGlucoseLevelDisabled(): boolean {
    const disabled = this.formGroup.controls.fastingPlasmaGlucoseLevelCheckbox.value === false;
    return disabled;
  }

  glycohemoglobinDisabled(): boolean {
    const disabled = this.formGroup.controls.glycohemoglobinCheckbox.value === false;
    return disabled;
  }

  conditionDisabled(): boolean {
    const disabled = this.fastingPlasmaGlucoseLevelDisabled() || this.glycohemoglobinDisabled();
    return disabled;
  }

  criteriaCheckboxChanged(checkboxName: string) {
    const fastingPlasmaGlucoseLevelSelected = this.formGroup.controls.fastingPlasmaGlucoseLevelCheckbox.value;
    const glycohemoglobinSelected = this.formGroup.controls.glycohemoglobinCheckbox.value;
    const deselectedLastCheckboxF = checkboxName === 'f' && !glycohemoglobinSelected;
    const deselectedLastCheckboxG =  checkboxName === 'g' && !fastingPlasmaGlucoseLevelSelected;
    if (deselectedLastCheckboxF || deselectedLastCheckboxG) {
      this.vennHealthDialogService.showOkDialog('Cannot deselect all criteria', 'At least 1 criteria selection is required', false);
      if (deselectedLastCheckboxF) {
        this.formGroup.controls.fastingPlasmaGlucoseLevelCheckbox.setValue(true);
      }
      if (deselectedLastCheckboxG) {
        this.formGroup.controls.glycohemoglobinCheckbox.setValue(true);
      }
      return;
    } else {
      this.updateSelections();
    }
  }

  showCriteriaSection(): boolean {
    const visible = this.childPopulationsSelectionModel && 
    !vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(this.childPopulationsSelectionModel.selectionValue);
    return visible;
  }

  updateSelections(): void {
    const currentSelections: SelectionsModel[] = [];

    if (this.childPopulationsSelectionModel) {
      currentSelections.push(this.childPopulationsSelectionModel);
    }

    if (this.showDiagnosisSelections() && this.childDiagnosisSelectionModel) {
      currentSelections.push(this.childDiagnosisSelectionModel);
    }
    
    const fastingPlasmaGlucoseLevelSelected = this.formGroup.controls.fastingPlasmaGlucoseLevelCheckbox.value;
    const glycohemoglobinSelected = this.formGroup.controls.glycohemoglobinCheckbox.value;

    // radio buttons
    var diagnosedDiabeticRetinopathyFormControl = this.formGroup.controls.diagnosedDiabeticRetinopathy;
    const diagnosedDiabeticRetinopathySelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: this.diagnosedDiabeticRetinopathyVariableName,
      controlType: ControlTypeEnum.RADIO,
      controlDisplayName: this.diagnosedDiabeticRetinopathyLabel,
      order: 1,
      selectionValue: diagnosedDiabeticRetinopathyFormControl.value,
      selectionDisplayName: this.diagnosedDiabeticRetinopathyRadioInputOptions.find(i => i.name === diagnosedDiabeticRetinopathyFormControl.value).displayName
    };
    currentSelections.push(diagnosedDiabeticRetinopathySelection);

    // checkboxes
    const currentFastingCheckboxSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: 'fastingPlasmaGlucoseLevelCheckbox',
      controlType: ControlTypeEnum.IGNORE_IN_UI,
      controlDisplayName: this.fastingPlasmaGlucoseLevelLabel,
      order: 2,
      selectionValue: fastingPlasmaGlucoseLevelSelected,
      selectionDisplayName: ''
    };
    currentSelections.push(currentFastingCheckboxSelection);

    const currentGlycohemoglobinCheckboxSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: 'glycohemoglobinCheckbox',
      controlType: ControlTypeEnum.IGNORE_IN_UI,
      controlDisplayName: this.glycohemoglobinLabel,
      order: 3,
      selectionValue: glycohemoglobinSelected,
      selectionDisplayName: ''
    };
    currentSelections.push(currentGlycohemoglobinCheckboxSelection);

    // conditional inputs
    if (fastingPlasmaGlucoseLevelSelected) {
      const currentFastingPlasmaGlucoseLevelSelection: SelectionsModel = {
        indicationName: this.indication.name,
        controlName: 'fastingPlasmaGlucoseLevel',
        controlType: ControlTypeEnum.TEXT,
        controlDisplayName: this.fastingPlasmaGlucoseLevelLabel,
        order: 4,
        selectionValue: this.formGroup.controls.fastingPlasmaGlucoseLevel.value,
        selectionDisplayName: ''
      };
      currentSelections.push(currentFastingPlasmaGlucoseLevelSelection);
    }

    if (fastingPlasmaGlucoseLevelSelected && glycohemoglobinSelected) {
      const currentConditionSelection: SelectionsModel = {
        indicationName: this.indication.name,
        controlName: 'condition',
        controlType: ControlTypeEnum.CUSTOM,
        controlDisplayName: '',
        order: 5,
        selectionValue: this.formGroup.controls.condition.value,
        selectionDisplayName: ''
      };
      currentSelections.push(currentConditionSelection);
    }

    if (glycohemoglobinSelected) {
      const currentGlycohemoglobinSelection: SelectionsModel = {
        indicationName: this.indication.name,
        controlName: 'glycohemoglobin',
        controlType: ControlTypeEnum.TEXT,
        controlDisplayName: this.glycohemoglobinLabel,
        order: 6,
        selectionValue: this.formGroup.controls.glycohemoglobin.value,
        selectionDisplayName: ''
      };
      currentSelections.push(currentGlycohemoglobinSelection);
    }
    
    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }

}
